import { useState, useRef } from 'react';
import { Upload, Trash2, Download, FileText, Image, File as FileIcon, Pen, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import {
  useUploadSubmittalFile,
  useDeleteSubmittalFile,
} from '@/hooks/useSubmittals';
import type { SubmittalFile } from '@/hooks/useSubmittals';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(contentType: string) {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf') || contentType?.includes('text')) return FileText;
  return FileIcon;
}

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

interface SubmittalFileListProps {
  submittalId: string;
  files: SubmittalFile[];
  /** Called when user clicks "Annotate" on a PDF file */
  onAnnotateFile?: (file: SubmittalFile) => void;
  /** The file ID currently being loaded for annotation (shows spinner) */
  annotatingFileId?: string | null;
}

export function SubmittalFileList({ submittalId, files, onAnnotateFile, annotatingFileId }: SubmittalFileListProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFile = useUploadSubmittalFile();
  const deleteFile = useDeleteSubmittalFile();

  const handleUpload = async (file: File) => {
    await uploadFile.mutateAsync({ submittalId, file });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    for (const file of droppedFiles) {
      await handleUpload(file);
    }
  };

  const handleDownload = async (file: SubmittalFile) => {
    await api.submittals.files.downloadFile(submittalId, file.id, file.file_name);
  };

  return (
    <div
      className={`border rounded-lg p-3 ${isDragging ? 'border-primary bg-primary/5' : 'border-dashed border-muted-foreground/25'}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) await handleUpload(file);
          e.target.value = '';
        }}
      />

      {files.length === 0 ? (
        <button
          className="w-full py-4 text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          {isDragging ? 'Drop file here' : 'Drag & drop or click to upload files'}
        </button>
      ) : (
        <div className="space-y-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              {files.length} file{files.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-3 h-3 mr-1" />
              Upload
            </Button>
          </div>

          {files.map((file) => {
            const Icon = getFileIcon(file.content_type);
            return (
              <div
                key={file.id}
                className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/50 group"
              >
                <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate flex-1">{file.file_name}</span>
                <span className="text-xs text-muted-foreground">
                  Rev {file.revision}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatFileSize(file.file_size)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {timeAgo(file.created_at)}
                </span>
                <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                  {/* Annotate button — only for PDF files */}
                  {onAnnotateFile && file.content_type?.includes('pdf') && (
                    <button
                      className="p-1 hover:bg-muted rounded"
                      title="Annotate PDF"
                      onClick={() => onAnnotateFile(file)}
                      disabled={annotatingFileId === file.id}
                    >
                      {annotatingFileId === file.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Pen className="w-3 h-3" />
                      )}
                    </button>
                  )}
                  <button
                    className="p-1 hover:bg-muted rounded"
                    onClick={() => handleDownload(file)}
                  >
                    <Download className="w-3 h-3" />
                  </button>
                  <button
                    className="p-1 hover:bg-destructive/10 hover:text-destructive rounded"
                    onClick={() => deleteFile.mutate({ submittalId, fileId: file.id })}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
